import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SetIndigenousNationModeAction } from '@app/app-state/actions/app-state.action';
import { AppState } from '@app/app-state/models/app-state';
import { ApplicationDataService } from '@app/services/application-data.service';
import { LicenseDataService } from '@app/services/license-data.service';
import {
  CRS_RENEWAL_LICENCE_TYPE_NAME,
  LIQUOR_RENEWAL_LICENCE_TYPE_NAME
} from '@components/licences/licences.component';
import { faExchangeAlt, faPencilAlt, faPlus, faShoppingCart, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Account } from '@models/account.model';
import { ApplicationLicenseSummary } from '@models/application-license-summary.model';
import { ApplicationSummary } from '@models/application-summary.model';
import { ApplicationStatuses, ApplicationType, ApplicationTypeNames } from '@models/application-type.model';
import { Application } from '@models/application.model';
import { Store } from '@ngrx/store';
import { FeatureFlagService } from '@services/feature-flag.service';
import { PaymentDataService } from '@services/payment-data.service';
import { UserDataService } from '@services/user-data.service';
import { FormBase } from '@shared/form-base';
import { differenceInDays, startOfDay, startOfToday } from 'date-fns';
import { Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

export const UPLOAD_FILES_MODE = 'UploadFilesMode';
export const INCOMPLETE = 'Incomplete';

const ACTIVE = 'Active';
const RENEWAL_DUE = 'Renewal Due';

@Component({
  selector: 'app-applications-and-licences',
  templateUrl: './applications-and-licences.component.html',
  styleUrls: ['./applications-and-licences.component.scss']
})
export class ApplicationsAndLicencesComponent extends FormBase implements OnInit {
  faPencilAlt = faPencilAlt;
  faTrashAlt = faTrashAlt;
  faPlus = faPlus;
  faShoppingCart = faShoppingCart;
  faExchangeAlt = faExchangeAlt;
  inProgressApplications: ApplicationSummary[] = [];

  readonly ACTIVE = ACTIVE;
  readonly RENEWAL_DUE = RENEWAL_DUE;

  busy: Subscription;
  @Input()
  applicationInProgress: boolean;
  @Input()
  account: Account;
  @Output()
  marketerApplicationExists = new EventEmitter<boolean>();
  @Output()
  hasLicenceChange = new EventEmitter<boolean>();
  dataLoaded = false;
  private _loadedApplications: ApplicationSummary[] = [];
  licencePresentLabel: string;
  licenceAbsentLabel: string;
  submittedApplications = 8;
  marketerExists: boolean;
  nonMarketerExists: boolean;
  ApplicationTypeNames = ApplicationTypeNames;
  licenceTransferFeatureOn = false;
  licenseeChangeFeatureOn: boolean;
  liquorOne: boolean;
  liquorTwo: boolean;
  liquorThree: boolean;
  RLRS: boolean;
  F2G: boolean;
  startMarketingOngoing: boolean;
  startCateringOngoing: boolean;
  startFPOngoing: boolean;
  startLPOngoing: boolean;
  startLPCOngoing: boolean;
  startMfgOngoing: boolean;
  startUBVOngoing: boolean;
  startRASOngoing: boolean;
  startCRSOngoing: boolean;
  startPRSOngoing: boolean;
  startRLRSOngoing: boolean;
  startAgentOngoing: boolean;
  startF2GOngoing: boolean;
  startEthylOngoing: boolean;

  /**
   * The "in-progress" statuses of a Legal Entity Review application.
   */
  leReviewInProgressStatuses: string[] = [
    ApplicationStatuses.Intake,
    ApplicationStatuses.Incomplete,
    ApplicationStatuses.Submitted,
    ApplicationStatuses.UnderReview,
    ApplicationStatuses.LicenseeActionRequired,
    ApplicationStatuses.ApplicationAssessment
  ];
  hasInProgressLeReviewApplication: boolean = true;

  constructor(
    private userDataService: UserDataService,
    private applicationDataService: ApplicationDataService,
    private licenceDataService: LicenseDataService,
    private router: Router,
    private store: Store<AppState>,
    private snackBar: MatSnackBar,
    private paymentService: PaymentDataService,
    public featureFlagService: FeatureFlagService,
    public dialog: MatDialog
  ) {
    super();
    if (featureFlagService.featureOn('Marketer')) {
      this.licencePresentLabel = '';
      this.licenceAbsentLabel = '';
    } else {
      this.licencePresentLabel = '';
      this.licenceAbsentLabel = '';
    }
    featureFlagService
      .featureOn('LicenceTransfer')
      .pipe(takeWhile(() => this.componentActive))
      .subscribe((featureOn: boolean) => {
        this.licenceTransferFeatureOn = featureOn;
      });
    featureFlagService.featureOn('LicenseeChanges').subscribe((x) => (this.licenseeChangeFeatureOn = x));
    featureFlagService.featureOn('LiquorOne').subscribe((x) => (this.liquorOne = x));
    featureFlagService.featureOn('LiquorTwo').subscribe((x) => (this.liquorTwo = x));
    featureFlagService.featureOn('LiquorThree').subscribe((x) => (this.liquorThree = x));
    featureFlagService.featureOn('RLRS').subscribe((x) => (this.RLRS = x));
    featureFlagService.featureOn('F2G').subscribe((x) => (this.F2G = x));
  }

  ngOnInit() {
    this.displayApplications();
    this.startMarketingOngoing = false;
    this.applicationDataService
      .getSubmittedCannabisRetailStoreApplicationCount()
      .subscribe((value) => (this.submittedApplications = value));
  }

  isApprovedByLGAndNotSubmitted(item: ApplicationSummary): boolean {
    const result = item && item.lgHasApproved && !item.isPaid && item.isApplicationComplete !== 'Yes';
    return result;
  }

  /**
   *
   */
  private displayApplications() {
    this.dataLoaded = false;
    this._loadedApplications = [];
    this.inProgressApplications = [];

    // Applications: show "In Progress Applications" tab as soon as this call returns
    const appsSub = this.applicationDataService
      .getAllCurrentApplications()
      .pipe(takeWhile(() => this.componentActive))
      .subscribe((applications) => {
        this._loadedApplications = applications;
        this.checkIndigenousNationState(applications);
        applications
          .filter(
            (app) => ['Approved', 'Renewal Due', 'Payment Required', 'Active'].indexOf(app.applicationStatus) === -1
          )
          .forEach((application: ApplicationSummary) => {
            if (application.applicationTypeName != 'Outstanding Prior Balance Invoice - LIQ') {
              this.inProgressApplications.push(application);
            }
          });
        this.hasInProgressLeReviewApplication = this.userHasInProgressLegalEntityReviewApplication(applications);
        this.dataLoaded = true;
      });

    // Licences: fires in parallel; updates marketer flags and emits hasLicence when ready
    const licSub = this.licenceDataService
      .getAllCurrentLicenses()
      .pipe(takeWhile(() => this.componentActive))
      .subscribe((licenses) => {
        const applications = this._loadedApplications;
        // LCSD-6843: 2024-03-01 waynezen; executes asynchronously on long-running thread
        this.marketerExists = this.marketerExistsFunc(applications, licenses);
        this.nonMarketerExists =
          applications
            .filter((item) => item.applicationTypeName === ApplicationTypeNames.CannabisRetailStore)
            .map((item) => item as any)
            .concat(licenses.filter((item) => item.licenceTypeName !== ApplicationTypeNames.Marketer)).length > 0;
        this.hasLicenceChange.emit(licenses.length > 0);
      });

    // LCSD-6843: 2024-03-01 waynezen; executes immediately; set marketerExists flag here first which hides the Cannabis tiles initially
    this.marketerExists = true;

    this.subscriptionList.push(appsSub, licSub);
  }

  /**
   * Checks if the user has an in-progress Legal Entity Review application
   *
   * @private
   * @param {ApplicationSummary[]} applications
   * @return {*}  {boolean} `true` if the user has an in-progress Legal Entity Review application, `false` otherwise
   */
  private userHasInProgressLegalEntityReviewApplication(applications: ApplicationSummary[]): boolean {
    return applications.some(
      (app) =>
        app.applicationTypeName === ApplicationTypeNames.LegalEntityReview &&
        this.leReviewInProgressStatuses.includes(app.applicationStatus)
    );
  }

  // LCSD-6843: 2024-03-01 waynezen
  private marketerExistsFunc(applications: ApplicationSummary[], licenses: ApplicationLicenseSummary[]): boolean {
    const isMarketerExist =
      applications
        .filter((item) => item.applicationTypeName === ApplicationTypeNames.Marketer)
        .map((item) => item as any)
        .concat(licenses.filter((item) => item.licenceTypeName === ApplicationTypeNames.Marketer)).length > 0;

    return isMarketerExist;
  }

  uploadMoreFiles(application: Application) {
    this.router.navigate([`/application/${application.id}`, { mode: UPLOAD_FILES_MODE }]);
  }

  resolveIncompleteness(application: ApplicationSummary) {
    if (application.applicationTypeName === ApplicationTypeNames.LegalEntityReview) {
      this.router.navigate([`/legal-entity-review/${application.id}`]);
    } else {
      this.router.navigate([`/application/${application.id}`, { mode: INCOMPLETE }]);
    }
  }

  checkIndigenousNationState(applications: ApplicationSummary[]) {
    if (applications.find((a) => a.isIndigenousNation)) {
      this.store.dispatch(new SetIndigenousNationModeAction(true));
    }
  }

  /**
   *
   * @param applicationId
   * @param establishmentName
   * @param applicationName
   */
  cancelApplication(applicationId: string, establishmentName: string, applicationName: string) {
    const dialogConfig = {
      disableClose: true,
      autoFocus: true,
      width: '400px',
      height: '200px',
      data: {
        establishmentName: establishmentName,
        applicationName: applicationName
      }
    };

    // open dialog, get reference and process returned data from dialog
    const dialogRef = this.dialog.open(ApplicationCancellationDialogComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(takeWhile(() => this.componentActive))
      .subscribe((cancelApplication) => {
        if (cancelApplication) {
          // delete the application.
          this.busy = this.applicationDataService
            .cancelApplication(applicationId)
            .pipe(takeWhile(() => this.componentActive))
            .subscribe(() => {
              this.displayApplications();
            });
        }
      });
  }

  doAction(licence: ApplicationLicenseSummary, actionName: string) {
    const actionApplication = licence.actionApplications.find(
      (app) => app.applicationTypeName === actionName && app.applicationStatus !== 'Active'
    );
    if (actionApplication && !actionApplication.isPaid) {
      this.router.navigateByUrl(`/account-profile/${actionApplication.applicationId}`);
    } else if (actionApplication && actionApplication.isPaid) {
      this.snackBar.open('Application already submitted', 'Fail', { duration: 3500, panelClass: ['red-snackbar'] });
    } else {
      this.busy = this.licenceDataService
        .createApplicationForActionType(licence.licenseId, actionName)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe(
          (data) => {
            this.router.navigateByUrl(`/account-profile/${data.id}`);
          },
          () => {
            this.snackBar.open(`Error running licence action for ${actionName}`, 'Fail', {
              duration: 3500,
              panelClass: ['red-snackbar']
            });
            console.log('Error starting a Change Licence Location Application');
          }
        );
    }
  }

  planStoreOpening(licence: ApplicationLicenseSummary) {
    const crsApplication = licence.actionApplications.find(
      (app) => app.applicationTypeName === ApplicationTypeNames.CannabisRetailStore
    );
    if (crsApplication) {
      this.router.navigate([`/store-opening/${crsApplication.applicationId}`]);
    } else {
      this.snackBar.open('Unable to find CRS Application', 'Fail', { duration: 3500, panelClass: ['red-snackbar'] });
    }
  }

  payLicenceFee(application: ApplicationSummary) {
    // locate the application associated with the issuance of this licence
    // const application = licence.actionApplications.find(app => app.applicationTypeName === licence.licenceTypeName);
    if (application) {
      this.busy = this.paymentService
        .getInvoiceFeePaymentSubmissionUrl(application.id)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe(
          (res) => {
            const data = res as any;
            window.location.href = data.url;
          },
          (err) => {
            if (err === 'Payment already made') {
              this.snackBar.open('Licence Fee payment has already been made, please refresh the page.', 'Fail', {
                duration: 3500,
                panelClass: ['red-snackbar']
              });
            }
          }
        );
    }
  }

  startNewLicenceApplication() {
    this.startCRSOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Cannabis Retail Store',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.CannabisRetailStore } as ApplicationType,
      account: this.account,
      servicehHoursStandardHours: false,
      serviceHoursSundayOpen: '09:00',
      serviceHoursMondayOpen: '09:00',
      serviceHoursTuesdayOpen: '09:00',
      serviceHoursWednesdayOpen: '09:00',
      serviceHoursThursdayOpen: '09:00',
      serviceHoursFridayOpen: '09:00',
      serviceHoursSaturdayOpen: '09:00',
      serviceHoursSundayClose: '23:00',
      serviceHoursMondayClose: '23:00',
      serviceHoursTuesdayClose: '23:00',
      serviceHoursWednesdayClose: '23:00',
      serviceHoursThursdayClose: '23:00',
      serviceHoursFridayClose: '23:00',
      serviceHoursSaturdayClose: '23:00'
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        // reload the user to cause the eligibility disclosure to show if needed
        this.userDataService.loadUserToStore().then(() => {});
        this.router.navigateByUrl(`/account-profile/${data.id}`);
        this.startCRSOngoing = false;
      },
      () => {
        this.snackBar.open('Error starting a New Licence Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startCRSOngoing = false;
        console.log('Error starting a New Licence Application');
      }
    );
  }

  startNewPRSLicenceApplication() {
    this.startPRSOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Producer Retail Store',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.ProductionRetailStore } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        // reload the user to cause the eligibility disclosure to show if needed
        this.router.navigateByUrl(`/account-profile/${data.id}`);
        this.startPRSOngoing = false;
      },
      () => {
        this.snackBar.open('Error starting a New Licence Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startPRSOngoing = false;
        console.log('Error starting a New Licence Application');
      }
    );
  }

  startNewMarketerApplication() {
    this.startMarketingOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Marketing',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.Marketer } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        this.startMarketingOngoing = false;
        this.router.navigateByUrl(`/account-profile/${data.id}`);
      },
      () => {
        this.snackBar.open('Error starting a New Marketer Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startMarketingOngoing = false;
        console.log('Error starting a New Marketer Application');
      }
    );
  }

  startNewF2GApplication() {
    this.startF2GOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Farm to Gate',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.F2G } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        this.startF2GOngoing = false;
        this.router.navigateByUrl(`/account-profile/${data.id}`);
      },
      () => {
        this.snackBar.open('Error starting a New Farm to Gate Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startF2GOngoing = false;
        console.log('Error starting a New Farm to Gate Application');
      }
    );
  }

  startNewCateringApplication() {
    this.startCateringOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Catering',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.Catering } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startCateringOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Catering Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startCateringOngoing = false;
        console.log('Error starting a Catering Application');
      }
    );
  }

  startNewAgentApplication() {
    this.startAgentOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Agent',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.Agent } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startAgentOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting an Agent Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startAgentOngoing = false;
        console.log('Error starting Agent Application');
      }
    );
  }

  startNewMfgApplication() {
    this.startMfgOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Manufacturer',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.MFG } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startMfgOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Manufacturer Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startMfgOngoing = false;
        console.log('Error starting a Manufacturer Application');
      }
    );
  }

  startNewFPApplication() {
    this.startFPOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Food Primary',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.FP } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startFPOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Food Primary Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startFPOngoing = false;
        console.log('Error starting a Food Primary Application');
      }
    );
  }

  startNewLPApplication(LPOngoing: boolean) {
    this.startLPOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Liquor Primary',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.LP } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startLPOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Liquor Primary Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startLPOngoing = false;
        console.log('Error starting a Liquor Primary Application');
      }
    );
  }

  startNewLPCApplication() {
    this.startLPCOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Liquor Primary Club',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.LPC } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startLPCOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Liquor Primary Club Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startLPOngoing = false;
        console.log('Error starting a Liquor Primary Club Application');
      }
    );
  }

  startNewRASApplication() {
    this.startRASOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Rural Agency Store',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.RAS } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startRASOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Rural Agency Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startRASOngoing = false;
        console.log('Error starting a Rural Agency Application');
      }
    );
  }
  startNewEthylApplication() {
    this.startEthylOngoing = true;
    const newLicenceApplicationData = {
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.ETHYL } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startEthylOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Ethyl Alcohol Permit', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startEthylOngoing = false;
        console.log('Error starting a Ethyl Alcohol Permit');
      }
    );
  }

  startNewRLRSApplication() {
    this.startRLRSOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'Rural Licensee Retail Store',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.RLRS } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startRASOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Rural Licensee Retail Store Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startRLRSOngoing = false;
        console.log('Error starting a Rural Licensee Retail Store Application');
      }
    );
  }

  startNewUBVApplication() {
    this.startUBVOngoing = true;
    const newLicenceApplicationData = {
      licenseType: 'UBrew and UVin',
      applicantType: this.account.businessType,
      applicationType: { name: ApplicationTypeNames.UBV } as ApplicationType,
      account: this.account
    } as Application;
    // newLicenceApplicationData. = this.account.businessType;
    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/account-profile/${data.id}`];
        this.startUBVOngoing = false;
        this.router.navigate(route);
      },
      () => {
        this.snackBar.open('Error starting a Rural Agency Store Application', 'Fail', {
          duration: 3500,
          panelClass: ['red-snackbar']
        });
        this.startUBVOngoing = false;
        console.log('Error starting a Rural Agency Store Application');
      }
    );
  }

  startRenewal(licence: ApplicationLicenseSummary) {
    const renewalApplication = licence.actionApplications.find((app) => app.applicationTypeName === 'CRS Renewal');
    if (renewalApplication && !renewalApplication.isPaid) {
      this.router.navigateByUrl(`/renew-crs-licence/application/${renewalApplication.applicationId}`);
    } else if (renewalApplication && renewalApplication.isPaid) {
      this.snackBar.open('Renewal application already submitted', 'Fail', {
        duration: 3500,
        panelClass: ['red-snackbar']
      });
    } else {
      const actionName = 'CRS Renewal';
      this.busy = this.licenceDataService
        .createApplicationForActionType(licence.licenseId, actionName)
        .pipe(takeWhile(() => this.componentActive))
        .subscribe(
          (data) => {
            this.router.navigateByUrl(`/renew-crs-licence/application/${data.id}`);
          },
          () => {
            this.snackBar.open(`Error running licence action for ${actionName}`, 'Fail', {
              duration: 3500,
              panelClass: ['red-snackbar']
            });
            console.log('Error starting a Change Licence Location Application');
          }
        );
    }
  }

  startEndorsementApplication(application: Application, endorsementType: string) {
    const newLicenceApplicationData = {
      parentApplicationId: application.id,
      licenseType: application.applicationTypeName,
      applicantType: this.account.businessType,
      applicationType: { name: endorsementType } as ApplicationType,
      establishmentAddress: application.establishmentAddress,
      establishmentAddressCity: application.establishmentAddressCity,
      establishmentAddressPostalCode: application.establishmentAddressPostalCode,
      establishmentAddressStreet: application.establishmentAddressStreet,
      establishmentParcelId: application.establishmentParcelId,
      establishmentName: application.establishmentName,
      establishmentPhone: application.establishmentPhone,
      establishmentEmail: application.establishmentEmail,
      policeJurisdictionId: application.policeJurisdictionId,
      indigenousNationId: application.indigenousNationId,
      account: this.account
    } as Application;

    this.busy = this.applicationDataService.createApplication(newLicenceApplicationData).subscribe(
      (data) => {
        const route: any[] = [`/application/${data.id}`];

        this.router.navigate(route);
      },
      () => {
        this.snackBar.open(`Error starting the Application`, 'Fail', { duration: 3500, panelClass: ['red-snackbar'] });
      }
    );
  }

  isAboutToExpire(expiryDate: string) {
    const now = startOfToday();
    const expiry = startOfDay(new Date(expiryDate));
    const diff = differenceInDays(now, expiry) + 1;
    return diff <= 60 || expiry < now;
  }

  licenceHasExpired(expiryDate: string) {
    const now = startOfToday();
    const expiry = startOfDay(new Date(expiryDate));
    return expiry < now;
  }

  getRenewalType(applicationType: string): string {
    let licenceType = '';
    if (
      applicationType === ApplicationTypeNames.CRSRenewal ||
      applicationType === ApplicationTypeNames.MarketingRenewal
    ) {
      licenceType = CRS_RENEWAL_LICENCE_TYPE_NAME;
    } else if (applicationType === ApplicationTypeNames.LiquorRenewal) {
      licenceType = LIQUOR_RENEWAL_LICENCE_TYPE_NAME;
    }
    return licenceType;
  }

  CRSElligible(): boolean {
    switch (this.account && this.account.businessType) {
      case 'University':
      case 'Church':
        return false;
      default:
        return true;
    }
  }

  getApplicationLink(item: ApplicationSummary) {
    if (this.isPermanentChangeToLicenceAsAResultOfLegalEntityReview(item)) {
      return `/legal-entity-review-permanent-change-to-a-licensee/${item.id}`;
    } else if (this.isPermanentChangeToLicenceApplication(item)) {
      return `/permanent-change-to-a-licensee/${item.id}`;
    } else if (this.isLegalEntityReviewApplication(item)) {
      return `/legal-entity-review/${item.id}`;
    } else {
      return `/account-profile/${item.id}`;
    }
  }

  /**
   * Checks if the application summary is for a permanent change to a license application.
   *
   * @param {ApplicationSummary} ApplicationSummary
   * @return {*}  {boolean}
   */
  isPermanentChangeToLicenceApplication(ApplicationSummary: ApplicationSummary): boolean {
    return ApplicationSummary.applicationTypeName === ApplicationTypeNames.PermanentChangeToALicensee;
  }

  /**
   * Checks if the application summary is for a permanent change to a license as a result of a legal entity review.
   *
   * Note: This is a regular Permanent Change to a Licensee application that has been created as a result of a
   * legal entity review, and has a binding to the corresponding legal entity review application.
   *
   * @param {ApplicationSummary} ApplicationSummary
   * @return {*}  {boolean}
   */
  isPermanentChangeToLicenceAsAResultOfLegalEntityReview(ApplicationSummary: ApplicationSummary): boolean {
    return (
      ApplicationSummary.applicationTypeName === ApplicationTypeNames.PermanentChangeToALicensee &&
      // Check if the PCL application is linked to a legal entity review application.
      ApplicationSummary.applicationExtension?.relatedLeOrPclApplicationId !== undefined &&
      ApplicationSummary.applicationExtension?.relatedLeOrPclApplicationId !== null
    );
  }

  /**
   * Checks if the application summary is for a legal entity review application.
   *
   * @param {ApplicationSummary} ApplicationSummary
   * @return {*}  {boolean}
   */
  isLegalEntityReviewApplication(ApplicationSummary: ApplicationSummary): boolean {
    return ApplicationSummary.applicationTypeName === ApplicationTypeNames.LegalEntityReview;
  }

  /**
   * Checks if the application summary is for any other application.
   *
   * @param {ApplicationSummary} ApplicationSummary
   * @return {*}  {boolean}
   */
  isOtherApplication(ApplicationSummary: ApplicationSummary): boolean {
    return (
      !this.isPermanentChangeToLicenceApplication(ApplicationSummary) &&
      !this.isLegalEntityReviewApplication(ApplicationSummary) &&
      !this.isPermanentChangeToLicenceAsAResultOfLegalEntityReview(ApplicationSummary)
    );
  }

  isCompleteActionVisible(applicationSummary: ApplicationSummary): boolean {
    // Hide for specific renewal and catering applications
    if (
      [ApplicationTypeNames.CRSRenewal, ApplicationTypeNames.Catering, ApplicationTypeNames.LiquorRenewal].includes(
        applicationSummary.applicationTypeName as ApplicationTypeNames
      )
    ) {
      return false;
    }

    // Show for PCL as result of LE Review application
    if (this.isPermanentChangeToLicenceAsAResultOfLegalEntityReview(applicationSummary)) {
      return true;
    }

    // Hide for regular PCL applications when user has an in-progress LE Review application
    if (
      applicationSummary.applicationTypeName === ApplicationTypeNames.PermanentChangeToALicensee &&
      this.hasInProgressLeReviewApplication
    ) {
      return false;
    }

    // Show for all other applications
    return true;
  }
}

@Component({
  selector: 'app-application-cancellation-dialog',
  templateUrl: 'application-cancellation-dialog.html'
})
export class ApplicationCancellationDialogComponent {
  establishmentName: string;
  applicationName: string;

  constructor(
    public dialogRef: MatDialogRef<ApplicationCancellationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.applicationName = data.applicationName;
    this.establishmentName = data.establishmentName;
  }

  close() {
    this.dialogRef.close(false);
  }

  cancel() {
    this.dialogRef.close(true);
  }
}
